/**
 * Value outcomes — what reality said about accepted values (migration 823,
 * PRD-value-suggestion-agent.md §6.2).
 *
 * Reads `accepted_value_outcomes`; the only writer is the database function
 * `record_value_realization`, reached here through `recordValueRealization`.
 * `summarizeValueOutcomes` is the pure math the calibration dashboard and
 * the agent's Accuracy tab share: per attribute, how far accepted values
 * landed from realized ones, the agent beside the people.
 */

import { getSupabaseClient } from './supabaseClient';
import {
  summarizeValueOutcomes,
  type AttributeOutcomeSummary,
  type OriginSummary,
} from '../../supabase/functions/_shared/calibrationCore.ts';

export type ValueOrigin = 'agent' | 'person' | 'none';
export type RealizedSource = 'delivery' | 'connector' | 'person';

export interface ValueOutcome {
  id: string;
  workspaceId: string;
  itemId: string;
  attributeKey: string;
  scaleId: string | null;
  acceptedValue: unknown;
  acceptedOrigin: ValueOrigin;
  proposalId: string | null;
  realizedValue: unknown;
  realizedSource: RealizedSource;
  realizedAt: string;
  /** Distance on the scale between accepted and realized; null when either could not be placed. */
  error: number | null;
  deliveryId: string | null;
}

function rowToOutcome(row: Record<string, unknown>): ValueOutcome {
  return {
    id: row.id as string,
    workspaceId: row.workspace_id as string,
    itemId: row.item_id as string,
    attributeKey: row.attribute_key as string,
    scaleId: (row.scale_id as string | null) ?? null,
    acceptedValue: row.accepted_value ?? null,
    acceptedOrigin: (row.accepted_origin as ValueOrigin) ?? 'none',
    proposalId: (row.proposal_id as string | null) ?? null,
    realizedValue: row.realized_value,
    realizedSource: row.realized_source as RealizedSource,
    realizedAt: row.realized_at as string,
    error: row.error == null ? null : Number(row.error),
    deliveryId: (row.delivery_id as string | null) ?? null,
  };
}

export async function loadValueOutcomes(params: { workspaceId: string; itemId?: string; limit?: number }): Promise<ValueOutcome[]> {
  let q = getSupabaseClient()
    .from('accepted_value_outcomes')
    .select('*')
    .eq('workspace_id', params.workspaceId)
    .order('realized_at', { ascending: false })
    .limit(params.limit ?? 500);
  if (params.itemId) q = q.eq('item_id', params.itemId);
  const { data, error } = await q;
  if (error) {
    console.warn('[valueOutcomes] load failed:', error.message);
    return [];
  }
  return ((data ?? []) as Record<string, unknown>[]).map(rowToOutcome);
}

/** Record what it actually took, through the one write path. */
export async function recordValueRealization(params: {
  itemId: string;
  attributeKey: string;
  realizedValue: unknown;
  source: RealizedSource;
  deliveryId?: string | null;
  scaleId?: string | null;
}): Promise<{ data: ValueOutcome | null; error: string | null }> {
  const { data, error } = await getSupabaseClient().rpc('record_value_realization', {
    p_item_id: params.itemId,
    p_attribute_key: params.attributeKey,
    p_realized_value: params.realizedValue,
    p_source: params.source,
    p_delivery_id: params.deliveryId ?? null,
    p_scale_id: params.scaleId ?? null,
  });
  if (error) return { data: null, error: error.message };
  return { data: data ? rowToOutcome(data as Record<string, unknown>) : null, error: null };
}

// ── Summary ───────────────────────────────────────────────────────────────────
// The summary rule lives in one place, shared with the public API
// (GET /v1/calibration): supabase/functions/_shared/calibrationCore.ts.

export { summarizeValueOutcomes };
export type { OriginSummary, AttributeOutcomeSummary };
